import { useState, useEffect } from 'react';
import { loyaltyStats } from '@/mocks/loyalty';

const AnimatedCounter = ({ target, suffix = '' }: { target: number; suffix?: string }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const duration = 1800;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [target]);
  return <>{count.toLocaleString()}{suffix}</>;
};

const LoyaltyHero = () => {
  return (
    <section className="relative pt-28 pb-20 px-4 md:px-8 lg:px-12 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/3896f487b0b92783277068a060c73258.jpg"
          alt=""
          className="w-full h-full object-cover object-top"
          style={{ filter: 'brightness(0.25) saturate(1.4)' }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(5,5,8,0.5) 0%, rgba(5,5,8,0.7) 60%, #050508 100%)' }} />
      </div>

      {/* Glow orbs */}
      <div className="absolute top-20 left-1/4 w-80 h-80 rounded-full opacity-10" style={{ background: '#ff2d78', filter: 'blur(100px)' }} />
      <div className="absolute top-32 right-1/4 w-64 h-64 rounded-full opacity-10" style={{ background: '#f0ff00', filter: 'blur(80px)' }} />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-96 h-40 rounded-full opacity-15" style={{ background: '#00f5ff', filter: 'blur(60px)' }} />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* Left: Text */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-mono text-xs tracking-widest mb-6" style={{ background: 'rgba(240,255,0,0.08)', border: '1px solid rgba(240,255,0,0.25)', color: '#f0ff00' }}>
              <i className="ri-vip-crown-fill" />
              ПРОГРАММА ЛОЯЛЬНОСТИ
            </div>

            <h1 className="font-orbitron font-black text-4xl md:text-6xl text-white leading-tight mb-4">
              БОНУСНАЯ
              <span className="block" style={{ color: '#f0ff00', textShadow: '0 0 30px rgba(240,255,0,0.6)' }}>
                КАРТА
              </span>
              <span className="block text-white/80">NEON PHARMA</span>
            </h1>

            <p className="font-mono text-sm text-white/50 leading-relaxed mb-8 max-w-lg">
              Копите баллы с каждой покупки и оплачивайте ими до 99% стоимости товаров. Два уровня привилегий — Стандартный и Золотой. 1 балл = 1 рубль.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                className="whitespace-nowrap flex items-center justify-center gap-2 px-8 py-4 rounded-full font-orbitron font-bold text-sm tracking-wider cursor-pointer transition-all duration-300"
                style={{ background: '#f0ff00', color: '#050508', boxShadow: '0 0 30px rgba(240,255,0,0.5)' }}
              >
                <i className="ri-vip-crown-fill" />
                ПОЛУЧИТЬ КАРТУ
              </button>
              <button
                className="whitespace-nowrap flex items-center justify-center gap-2 px-8 py-4 rounded-full font-mono text-sm cursor-pointer transition-all duration-300"
                style={{ border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)' }}
              >
                <i className="ri-information-line" />
                Узнать подробнее
              </button>
            </div>
          </div>

          {/* Right: Card visual */}
          <div className="flex justify-center lg:justify-end">
            <div className="relative w-80 h-52">
              {/* Gold card (back) */}
              <div
                className="absolute top-6 right-0 w-72 h-44 rounded-2xl p-5 flex flex-col justify-between"
                style={{
                  background: 'linear-gradient(135deg, #1a1500 0%, #2d2200 50%, #1a1500 100%)',
                  border: '1px solid rgba(240,192,0,0.4)',
                  boxShadow: '0 0 40px rgba(240,192,0,0.2)',
                  transform: 'rotate(6deg)',
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-orbitron font-black text-xs tracking-widest" style={{ color: '#f0c000' }}>NEON PHARMA</span>
                  <i className="ri-vip-crown-fill text-lg" style={{ color: '#f0c000' }} />
                </div>
                <div>
                  <div className="font-mono text-[10px] text-white/30 mb-0.5">ЗОЛОТОЙ УРОВЕНЬ</div>
                  <div className="font-orbitron font-black text-2xl" style={{ color: '#f0c000', textShadow: '0 0 15px rgba(240,192,0,0.8)' }}>GOLD</div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] text-white/30">**** **** **** 4821</div>
                  <div className="font-mono text-[10px]" style={{ color: '#f0c000' }}>3% кэшбэк</div>
                </div>
              </div>

              {/* Cyan card (front) */}
              <div
                className="absolute top-0 left-0 w-72 h-44 rounded-2xl p-5 flex flex-col justify-between"
                style={{
                  background: 'linear-gradient(135deg, #0a2a2e 0%, #0d3d44 50%, #0a2a2e 100%)',
                  border: '1px solid rgba(0,245,255,0.4)',
                  boxShadow: '0 0 40px rgba(0,245,255,0.2)',
                  transform: 'rotate(-3deg)',
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-orbitron font-black text-xs tracking-widest" style={{ color: '#00f5ff' }}>NEON PHARMA</span>
                  <i className="ri-vip-crown-line text-lg" style={{ color: '#00f5ff' }} />
                </div>
                <div>
                  <div className="font-mono text-[10px] text-white/30 mb-0.5">СТАНДАРТНЫЙ УРОВЕНЬ</div>
                  <div className="font-orbitron font-black text-2xl" style={{ color: '#00f5ff', textShadow: '0 0 15px rgba(0,245,255,0.8)' }}>STANDARD</div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] text-white/30">**** **** **** 1337</div>
                  <div className="font-mono text-[10px]" style={{ color: '#00f5ff' }}>1% кэшбэк</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-16">
          {loyaltyStats.map((stat, i) => (
            <div
              key={i}
              className="flex flex-col items-center text-center p-5 rounded-2xl"
              style={{ background: `${stat.color}08`, border: `1px solid ${stat.color}20` }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-3" style={{ background: `${stat.color}15`, border: `1px solid ${stat.color}30` }}>
                <i className={stat.icon} style={{ color: stat.color, fontSize: '18px' }} />
              </div>
              <div className="font-orbitron font-black text-xl mb-1" style={{ color: stat.color, textShadow: `0 0 15px ${stat.color}60` }}>
                {stat.value}
              </div>
              <div className="font-mono text-[11px] text-white/40 leading-snug">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LoyaltyHero;
